import _ from 'lodash';
import {t} from 'i18next';
import {LeftPanelResponse} from '../../../../../shared/interfaces';
import {buildTreeItems} from '../../../../../utils/reportUtils';
import {
  AccordionSection,
  ColumnConfig,
  FilterComponent,
  Reports,
  Section,
} from '../../config/reportTypes';
import {
  TagCountReportInputsType,
  TagCountReportReqPayload,
} from '../../../../../api/types/reportingApi.type';

type TreeItem = {
  id: string;
  fileType: string;
  children: TreeItem[];
};

const normalizeDate = (dateStr: string): string => {
  if (!dateStr) return dateStr;
  if (dateStr.split(' ').length <= 0) return dateStr;
  const separator = dateStr.includes('/') ? '/' : '-';
  const [year, month, day] = dateStr.split(' ')[0].split(separator);
  return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
};

const getFolderIds = (arr: TreeItem[]): string[] => {
  let folderIds: string[] = [];

  arr.forEach((item) => {
    if (item.fileType === 'folder') {
      folderIds.push(item.id);
      if (item.children && item.children.length > 0) {
        folderIds = folderIds.concat(getFolderIds(item.children));
      }
    }
  });
  return folderIds;
};

export const TAGS_COUNT_REPORT_TYPE = 'TCR005';
export const mergeApiDataIntoConfig = (
  report: Reports,
  apiData: LeftPanelResponse,
  savedReportData?: Record<string, any>
) => {
  const config = report.config;
  const configObject = {
    sections: config.sections.map((section: Section) => ({
      ...section,
      filters: section.filters.map((filter: AccordionSection) => {
        return {
          ...filter,
          components: filter.components.map((comp: FilterComponent) => {
            if (
              comp.input_type === 'checkboxList' &&
              filter.id === 'tagTypes'
            ) {
              const initialValues = _.get(
                savedReportData,
                'selectedTagClassification',
                []
              );

              const transformedData = _.get(
                apiData,
                'tagclassificationList',
                []
              ).map((reviewer) => ({
                label: reviewer.classification,
                value: initialValues.includes(reviewer.classificationID),
              }));
              return {
                ...comp,
                childLabels: transformedData,
                initialValues: transformedData.map((item) => item.value),
              };
            } else if (
              comp.input_type === 'multiSelectTreeViewList' &&
              filter.id === 'tags'
            ) {
              let initialValues = [];
              if (savedReportData && savedReportData.selectedTags) {
                initialValues = _.get(savedReportData, 'selectedTags', []).map(
                  (item: number) => item.toString()
                );
              }

              const transformedData = buildTreeItems(apiData.tagtree);
              return {
                ...comp,
                treeData: transformedData,
                defaultExpandedIds: getFolderIds(transformedData),
                initialValues: initialValues,
              };
            } else if (comp.input_type === 'datePicker') {
              if (comp.key === 'fromDate') {
                if (_.get(savedReportData, 'fromDateTime', '') !== '') {
                  const fromDate = normalizeDate(
                    _.get(savedReportData, 'fromDateTime', '')
                  );
                  return {
                    ...comp,
                    initialStartDate: fromDate,
                    initialValues: fromDate,
                  };
                }
                return {
                  ...comp,
                };
              }

              if (comp.key === 'toDate') {
                if (_.get(savedReportData, 'toDateTime', '') !== '') {
                  const toDate = normalizeDate(
                    _.get(savedReportData, 'toDateTime', '')
                  );
                  return {
                    ...comp,
                    initialEndDate: toDate,
                    initialValues: toDate,
                  };
                }
                return {
                  ...comp,
                };
              }
            }
            return comp;
          }),
        };
      }),
    })),
  };
  return {
    ...report,
    config: {
      selectionCriteria: config.selectionCriteria,
      sections: configObject.sections,
    },
  };
};

export const getTagByCountTagReportIdPayload = (
  appliedValue: Record<string, any>,
  apiData: LeftPanelResponse
): TagCountReportReqPayload => {
  const payload: TagCountReportInputsType = {
    SelectedTags: _.get(appliedValue, 'selectedTags', []),
    ReportTypeCode: TAGS_COUNT_REPORT_TYPE,
  };
  const selectedTagsTypes: string[] = [];

  _.get(appliedValue, 'selectedTagTypes', []).forEach(
    (isSelected: boolean, index: number) => {
      if (isSelected) {
        selectedTagsTypes.push(
          _.get(apiData, `tagclassificationList[${index}].classificationID`, '')
        );
      }
    }
  );
  payload.SelectedTagClassification = selectedTagsTypes;

  if (_.get(appliedValue, 'fromDate', '') !== '') {
    payload.FromDateTime = _.get(appliedValue, 'fromDate', '');
  }
  if (_.get(appliedValue, 'toDate', '') !== '') {
    payload.ToDateTime = _.get(appliedValue, 'toDate', '');
  }
  return {tagcountReportInputs: payload};
};

export const updateApplyButtonState = (
  report: Reports,
  filterValues: Record<string, any>
) => {
  const isTagsSelected = _.get(filterValues, 'selectedTags', []).length > 0;
  const isTagTypesSelected = _.get(filterValues, 'selectedTagTypes', []).some(
    (val: boolean) => val === true
  );
  const isFromDateInput = _.get(filterValues, 'fromDate', '') !== '';
  const isToDateInput = _.get(filterValues, 'toDate', '') !== '';

  const config = report.config;
  const configObject = {
    ...config,
    selectionCriteria: {
      ...config.selectionCriteria,
      actions: config.selectionCriteria.actions.map((action) => {
        if (
          action.input_type === 'button' &&
          action.actionKey === 'applyChanges'
        ) {
          return {
            ...action,
            disabled: !isTagsSelected,
          };
        } else if (
          action.input_type === 'button' &&
          action.actionKey === 'reset'
        ) {
          return {
            ...action,
            disabled:
              !isTagTypesSelected &&
              !isFromDateInput &&
              !isToDateInput &&
              !isTagsSelected,
          };
        } else {
          return action;
        }
      }),
    },
  };
  return {
    ...report,
    config: configObject,
  };
};

export const tagColumns: ColumnConfig[] = [
  {
    field: 'tagname',
    flex: 1,
    isVisible: true,
    headerName: t('reports.tagByCountTagReport.tagName'),
    cellClassName: 'first-column-cell ellipsis-cell',
    headerClassName: 'first-column-header',
  },
  {
    field: 'tagDocumentCount',
    flex: 1,
    isVisible: true,
    headerName: t('reports.tagByCountTagReport.tagDocCount'),
    cellClassName: 'ellipsis-cell',
    valueFormatter: (value: number) =>
      new Intl.NumberFormat().format(value ?? 0),
  },
  {
    field: 'tagPath',
    flex: 1,
    isVisible: true,
    headerName: t('reports.tagByCountTagReport.tagPath'),
    cellClassName: 'ellipsis-cell',
  },
];

export const getReportIdFromLocation = (location: any): number => {
  const key = location.pathname.split('/')[2];
  const pathParam = location.search;
  const urlParams = new URLSearchParams(pathParam);
  let id = urlParams.get('ReportId') ?? 0;
  if (key === 'tag-counts') {
    id =
      urlParams.get('ReportID') ??
      urlParams.get('reportId') ??
      urlParams.get('ReportId') ??
      urlParams.get('reportID') ??
      '';
  }
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getAppliedFilterValueObject = (report: Reports) => {
  const config = report.config;
  const filterObject: {[key: string]: any} = {};

  config.sections.forEach((section) => {
    section.filters.forEach((filter) => {
      filter.components.forEach((comp) => {
        if (comp.input_type === 'checkboxList' && filter.id === 'tagTypes') {
          filterObject.selectedTagTypes = (comp.childLabels ?? []).map(
            (item) => item.value
          );
        }

        if (
          comp.input_type === 'multiSelectTreeViewList' &&
          filter.id === 'tags' &&
          comp.initialValues
        ) {
          filterObject.selectedTags = comp.initialValues;
        }

        if (comp.input_type === 'datePicker') {
          if (comp.key === 'fromDate' && comp.initialStartDate) {
            filterObject.fromDate = comp.initialStartDate;
          }
          if (comp.key === 'toDate' && comp.initialEndDate) {
            filterObject.toDate = comp.initialEndDate;
          }
        }
      });
    });
  });
  return filterObject;
};
